import React, { useState, useEffect } from 'react';
import SortableTh from '../../components/ui/SortableTh';
import useTableFilters from '../../components/ui/useTableFilters';
import Pagination, { usePagination } from '../../components/ui/Pagination';
import CountChip from '../../components/ui/CountChip';
import { useToast } from '../../components/ui/Toast';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import UniversalNavbar from '../../components/ui/UniversalNavbar';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import NeedsAttention from './components/NeedsAttention';
import ActivityFeed from './components/ActivityFeed';
import MySubmissions from './components/MySubmissions';
import UpcomingDeadlines from './components/UpcomingDeadlines';
import Badge from '../../components/ui/Badge';
import BadgeDownloadButton from '../../components/badge/BadgeDownloadButton';
import conferenceService from '../../services/conferenceService';
import submissionService from '../../services/submissionService';
import reviewerService from '../../services/reviewerService';
import { formatDate } from '../../utils/formatters';

const UserDashboard = () => {
  // Replaces alert(): non-blocking, styled, translatable, and errors
  // persist until dismissed instead of vanishing.
  const { success: toastSuccess, error: toastError } = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation('dashboard');
  const { user, userProfile } = useAuth();
  const [allConferences, setAllConferences] = useState([]);
  const [managedConferences, setManagedConferences] = useState([]);
  const [draftConferences, setDraftConferences] = useState([]);
  const [userSubmissions, setUserSubmissions] = useState([]);
  const [pendingReviewerInvitations, setPendingReviewerInvitations] = useState([]);
  const [reviewerAssignments, setReviewerAssignments] = useState([]);
  // Reviews you still owe come before reviews you've submitted. A finished
  // review is a record, not a task, so it stops competing for attention with
  // one that's still outstanding. Deadline order within each group.
  const orderedAssignments = [...(reviewerAssignments || [])].sort((a, b) => {
    if (Boolean(a?.isDraft) !== Boolean(b?.isDraft)) return a?.isDraft ? -1 : 1;
    return new Date(a?.reviewDeadline || 0) - new Date(b?.reviewDeadline || 0);
  });
  const [chairWorkload, setChairWorkload] = useState([]);
  const [deadlines, setDeadlines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingSubmissions, setLoadingSubmissions] = useState(true);
  const [acceptingInvitationId, setAcceptingInvitationId] = useState(null);
  const [error, setError] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

  // Organisers with many conferences shouldn't get one enormous table.
  //
  // Sorted inline rather than via getSortedConferences(): that helper is
  // declared 250 lines below, and a hook must sit with the other hooks at the
  // top — calling it from here would hit the temporal dead zone.
  const confFilters = useTableFilters(managedConferences || [], React.useMemo(() => ({
    status: {
      label: t('managedConferences.columns.status', 'Status'),
      accessor: (c) => c?.status || '',
      format: (v) => String(v).replace(/_/g, ' ').replace(/^./, (x) => x.toUpperCase()),
    },
    role: {
      label: t('managedConferences.columns.role', 'Role'),
      // A person can hold several roles in one conference, so the joined string
      // is the honest value to filter on — "Chair, Reviewer" is a real, single
      // answer to "what am I here?", and splitting it would silently make the
      // OR-within-a-column rule mean something different for this one column.
      accessor: (c) => c?.roles?.join(', ') || '',
      emptyLabel: '—',
    },
  }), [t]));

  const sortedManagedConferences = React.useMemo(() => {
    const list = confFilters.filterRows(managedConferences || []);
    if (!sortConfig?.key) return list;
    const pick = (row) => {
      switch (sortConfig.key) {
        case 'name': return row?.name?.toLowerCase() ?? '';
        case 'status': return row?.status?.toLowerCase() ?? '';
        case 'deadline': return row?.deadlineRaw ? new Date(row.deadlineRaw).getTime() : 0;
        case 'role': return row?.roles?.join(', ')?.toLowerCase() ?? '';
        default: return '';
      }
    };
    const dir = sortConfig?.direction === 'asc' ? 1 : -1;
    return [...list].sort((a, b) => {
      const av = pick(a); const bv = pick(b);
      if (av < bv) return -1 * dir;
      if (av > bv) return 1 * dir;
      return 0;
    });
  }, [managedConferences, sortConfig, confFilters]);

  const { pageRows: confPageRows, ...confPager } = usePagination(sortedManagedConferences, 10);

  React.useEffect(() => {
    if (!user) navigate('/user-sign-in');
  }, [user, navigate]);

  useEffect(() => {
    if (user) loadConferencesByRole();
  }, [user]);

  useEffect(() => {
    if (user && location?.state?.refresh) {
      loadConferencesByRole();
      window.history?.replaceState({}, document.title);
    }
  }, [location?.state, user]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden && user) loadConferencesByRole();
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [user]);

  const loadConferencesByRole = async () => {
    try {
      setLoading(true);
      setLoadingSubmissions(true);
      const [
        createdData, chairData, reviewerData, authorData, organizerData,
        submissionConferences, confStats, subStats, userDeadlines, userSubmissionsData,
        reviewerInvitations, assignedPapers
      ] = await Promise.all([
        conferenceService?.getCreatedByUser(user?.id),
        conferenceService?.getByUserRole(user?.id, 'chair'),
        conferenceService?.getByUserRole(user?.id, 'reviewer'),
        conferenceService?.getByUserRole(user?.id, 'author'),
        conferenceService?.getByUserRole(user?.id, 'organizer'),
        conferenceService?.getConferencesWithUserSubmissions(user?.id),
        conferenceService?.getUserConferenceStats(user?.id),
        submissionService?.getUserStats(user?.id),
        conferenceService?.getUserDeadlines(user?.id),
        submissionService?.getAllUserSubmissionsWithReviewStatus(user?.id),
        conferenceService?.getMyPendingReviewerInvitations(),
        reviewerService?.getAssignedPapers(user?.id).catch(() => [])
      ]);

      const drafts = createdData?.filter(conf => conf?.status === 'draft');
      const published = createdData?.filter(conf => conf?.status !== 'draft');

      setDraftConferences(drafts?.map(conf => ({
        id: conf?.id, name: conf?.name, status: getStatusLabel(conf?.status),
        deadline: 'N/A', priority: 'Low', canManage: true
      })));

      const conferenceMap = new Map();
      const createdConferenceIds = new Set(createdData?.map(c => c?.id));

      const addConferenceRole = (conf, role) => {
        if (conf?.status === 'draft') return;
        const confId = conf?.id;
        if (conferenceMap?.has(confId)) {
          const existing = conferenceMap?.get(confId);
          if (!existing?.roles?.includes(role)) existing?.roles?.push(role);
          if (role === 'chair' || role === 'organizer') existing.canManage = true;
          if (conf?.paper_deadline) {
            const newDeadline = conf?.paper_deadline;
            if (!existing?.deadlineRaw || new Date(newDeadline) < new Date(existing?.deadlineRaw)) {
              existing.deadline = formatDate(newDeadline);
              existing.deadlineRaw = newDeadline;
              existing.priority = getPriority(newDeadline, conf?.status);
            }
          }
        } else {
          const deadline = conf?.paper_deadline || conf?.deadline;
          conferenceMap?.set(confId, {
            id: confId, name: conf?.name, status: getStatusLabel(conf?.status),
            deadline: formatDate(deadline), deadlineRaw: deadline,
            priority: getPriority(deadline, conf?.status), roles: [role],
            canManage: createdConferenceIds?.has(confId) || role === 'chair' || role === 'organizer'
          });
        }
      };

      published?.forEach(conf => addConferenceRole(conf, 'chair'));
      chairData?.forEach(conf => addConferenceRole(conf, 'chair'));
      organizerData?.forEach(conf => addConferenceRole(conf, 'organizer'));
      reviewerData?.forEach(conf => addConferenceRole(conf, 'reviewer'));
      authorData?.forEach(conf => addConferenceRole(conf, 'author'));
      submissionConferences?.forEach(conf => addConferenceRole(conf, 'author'));

      const allConferencesArray = Array.from(conferenceMap?.values());
      setAllConferences(allConferencesArray);

      const managedConferencesArray = allConferencesArray?.filter(conf =>
        conf?.roles?.some(role => role === 'chair' || role === 'organizer')
      );
      setManagedConferences(managedConferencesArray);

      // Chair/organiser obligations. Uses the same getSubmissions call the
      // conference dashboard already makes — no new query shape — and reduces
      // it to three counts per conference. Failures are swallowed per
      // conference so one bad conference can't blank the whole dashboard.
      loadChairWorkload(managedConferencesArray, createdData, chairData, organizerData);

      setDeadlines(userDeadlines?.slice(0, 5) || []);
      setUserSubmissions(userSubmissionsData || []);
      setPendingReviewerInvitations(reviewerInvitations || []);
      setReviewerAssignments(assignedPapers || []);
      setLoadingSubmissions(false);
    } catch (err) {
      setError(err?.message);
    } finally {
      setLoading(false);
      setLoadingSubmissions(false);
    }
  };

  // Light refresh: only reloads submissions without full-page loading state
  const refreshSubmissionsOnly = async () => {
    try {
      const data = await submissionService?.getAllUserSubmissionsWithReviewStatus(user?.id);
      setUserSubmissions(data || []);
    } catch (err) {
      console.error('Error refreshing submissions:', err);
    }
  };

  /**
   * Reduce each managed conference to the three things that actually block it:
   * papers with nobody assigned, reviews assigned but not returned, and papers
   * whose reviews are in but which carry no decision yet.
   *
   * Runs after the main load so the dashboard paints first — chair counts fill
   * in a moment later rather than holding up the whole page.
   */
  const loadChairWorkload = async (managed, ...conferenceSources) => {
    if (!managed?.length) { setChairWorkload([]); return; }

    // Pull real names/deadlines from whichever source list has the conference.
    const meta = new Map();
    conferenceSources?.flat()?.forEach(c => { if (c?.id) meta.set(c.id, c); });

    const results = await Promise.all(
      managed.map(async (conf) => {
        try {
          const subs = await conferenceService?.getSubmissions(conf?.id);
          if (!subs?.length) return null;

          const active = subs.filter(s => !['draft', 'withdrawn'].includes(s?.status));
          const needsReviewers = active.filter(
            s => s?.status === 'submitted' && (s?.reviewers?.length || 0) === 0
          ).length;
          const reviewsOutstanding = active.reduce(
            (n, s) => n + (s?.reviewers?.filter(r => r?.reviewStatus !== 'completed')?.length || 0), 0
          );
          const awaitingDecision = active.filter(
            s => s?.status === 'under_review'
              && (s?.reviewers?.length || 0) > 0
              && s.reviewers.every(r => r?.reviewStatus === 'completed')
          ).length;

          if (!needsReviewers && !reviewsOutstanding && !awaitingDecision) return null;

          const src = meta.get(conf?.id);
          const rawDeadline = src?.review_deadline || src?.paper_deadline || conf?.deadlineRaw;
          const days = rawDeadline
            ? Math.ceil((new Date(rawDeadline).getTime() - Date.now()) / 86400000)
            : null;

          return {
            id: conf?.id,
            name: src?.name || conf?.name,
            shortName: src?.short_name,
            needsReviewers,
            reviewsOutstanding,
            awaitingDecision,
            reviewDeadlineDays: days,
          };
        } catch (e) {
          console.error('Chair workload failed for conference', conf?.id, e);
          return null;
        }
      })
    );

    setChairWorkload(results.filter(Boolean));
  };

  const handleAcceptReviewerInvitation = async (invitationId) => {
    try {
      setAcceptingInvitationId(invitationId);
      const result = await conferenceService?.acceptReviewerInvitation(invitationId);
      await loadConferencesByRole();
      toastSuccess(`Reviewer invitation accepted${result?.conferenceName ? ` for ${result?.conferenceName}` : ''}.`);
    } catch (err) {
      toastError(err?.message || 'Failed to accept reviewer invitation.');
    } finally {
      setAcceptingInvitationId(null);
    }
  };

  const handleStartReview = (assignment) => {
    navigate(`/streamlined-review-workspace?submissionId=${assignment.submissionId}`);
  };

  const getStatusLabel = (status) => {
    const statusMap = {
      'draft': 'Draft', 'published': 'Published', 'submission_open': 'Open',
      'submission_closed': 'Closed', 'review_phase': 'Reviewing',
      'decision_made': 'Decided', 'completed': 'Completed', 'cancelled': 'Cancelled'
    };
    return statusMap?.[status] || status;
  };

  const getTranslatedStatus = (statusLabel) => {
    const translationMap = {
      'Draft': t('userDashboard.statuses.draft'), 'Published': t('userDashboard.statuses.published'),
      'Open': t('userDashboard.statuses.submissionOpen'), 'Closed': t('userDashboard.statuses.submissionClosed'),
      'Reviewing': t('userDashboard.statuses.reviewPhase'), 'Decided': t('userDashboard.statuses.decisionMade'),
      'Completed': t('userDashboard.statuses.completed'), 'Cancelled': t('userDashboard.statuses.cancelled')
    };
    return translationMap?.[statusLabel] || statusLabel;
  };


  // Conferences that have finished can't have an urgent deadline.
  const TERMINAL_STATUSES = ['completed', 'cancelled'];

  /**
   * Urgency is about deadlines you can still act on.
   *
   * The previous version returned 'High' for `daysUntil < 7`, which is also
   * true of every deadline in the past — so finished conferences displayed an
   * "Urgent" badge indefinitely. Anything already elapsed, or belonging to a
   * closed conference, carries no urgency.
   */
  const getPriority = (deadline, status) => {
    if (!deadline) return 'Low';
    if (TERMINAL_STATUSES.includes(status)) return 'None';

    const daysUntil = Math.ceil((new Date(deadline) - new Date()) / (1000 * 60 * 60 * 24));
    if (daysUntil < 0) return 'None';   // elapsed — not urgent, just over
    if (daysUntil < 7) return 'High';
    if (daysUntil < 30) return 'Medium';
    return 'Low';
  };

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig?.key === key && sortConfig?.direction === 'asc') direction = 'desc';
    setSortConfig({ key, direction });
  };

  const getSortedConferences = (list) => {
    if (!sortConfig?.key) return list;
    return [...list]?.sort((a, b) => {
      let aValue, bValue;
      switch (sortConfig?.key) {
        case 'name': aValue = a?.name?.toLowerCase(); bValue = b?.name?.toLowerCase(); break;
        case 'status': aValue = a?.status?.toLowerCase(); bValue = b?.status?.toLowerCase(); break;
        case 'deadline': aValue = a?.deadlineRaw ? new Date(a?.deadlineRaw)?.getTime() : 0; bValue = b?.deadlineRaw ? new Date(b?.deadlineRaw)?.getTime() : 0; break;
        case 'role': aValue = a?.roles?.join(', ')?.toLowerCase(); bValue = b?.roles?.join(', ')?.toLowerCase(); break;
        default: return 0;
      }
      if (aValue < bValue) return sortConfig?.direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortConfig?.direction === 'asc' ? 1 : -1;
      return 0;
    });
  };

  // A bespoke SortHeader used to live here: a clickable <th> with a chevron and
  // no aria-sort, so screen readers were never told the table was sorted or in
  // which direction. Its state shape already matched useSort's, so it's just
  // the shared primitive now — same behaviour, correct semantics, one
  // implementation instead of two.

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Icon name="Loader" size={24} className="animate-spin text-primary" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <Icon name="AlertCircle" size={24} className="text-error mx-auto mb-2" />
          <p className="text-sm text-error mb-3">{error}</p>
          <Button size="sm" onClick={loadConferencesByRole}>{t('userDashboard.retry')}</Button>
        </div>
      </div>
    );
  }


  return (
    <div className="min-h-screen bg-background bg-mesh">
      <UniversalNavbar />
      <main className="py-6 px-4 sm:px-6 lg:px-8">
        <div className="page-shell">
          {/* Header */}
          <div className="mb-6">
            <h1 className="text-2xl font-bold text-foreground tracking-tight">
              {t('userDashboard.welcomeBack', { name: userProfile?.firstName || userProfile?.full_name || 'User' })}
            </h1>
            <p className="text-sm text-muted-foreground mt-1">{t('userDashboard.subtitle')}</p>
          </div>

          {/* The page's one job: what is waiting on this person, across every
              conference and every role they hold. Counts are context and now sit
              underneath it — a dashboard that opens on "0 / 0 / 0 / 2" answers a
              question nobody asked. */}
          <NeedsAttention
            reviewerAssignments={reviewerAssignments}
            userSubmissions={userSubmissions}
            pendingInvitations={pendingReviewerInvitations}
            chairWorkload={chairWorkload}
            onAcceptInvitation={handleAcceptReviewerInvitation}
            acceptingInvitationId={acceptingInvitationId}
            loading={loading}
          />

          {/* The metric card row that used to sit here is gone. Four numbers in
              boxes took the top of the page to report figures nobody can act
              on, and pushed the actual work below the fold. Each count now
              lives on its own table, where it means something. */}

          {pendingReviewerInvitations?.length > 0 && (
            <div className="bg-card border border-border mb-6 overflow-hidden shadow-sm">
              <div className="px-4 py-3 border-b border-border bg-muted/40">
                <div className="flex items-center gap-2">
                  <Icon name="Mail" size={16} className="text-warning" />
                  <h2 className="text-sm font-semibold text-foreground">{t('userDashboard.reviewerInvitations.title', 'Reviewer invitations')}</h2>
                  <CountChip count={pendingReviewerInvitations?.length} tone="attention" />
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  {t('userDashboard.reviewerInvitations.note', 'Review invitations only activate after you explicitly accept them.')}
                </p>
              </div>
              <div className="divide-y divide-border">
                {pendingReviewerInvitations?.map((invitation) => (
                  <div key={invitation?.id} className="px-4 py-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-foreground truncate">
                        {invitation?.conferenceName}
                      </p>
                      <p className="text-xs text-muted-foreground mt-1">
                        {invitation?.invitedByName
                          ? `Invited by ${invitation?.invitedByName}`
                          : 'You have been invited to join this conference as a reviewer.'}
                      </p>
                      <p className="text-xs text-muted-foreground mt-1">
                        Accepting this invitation adds you to the reviewer pool for future paper assignments.
                      </p>
                    </div>
                    <div className="flex items-center gap-2 sm:flex-shrink-0">
                      <Button
                        variant="outline"
                        size="xs"
                        onClick={() => navigate(`/conference-landing-page?conferenceId=${invitation?.conferenceId}`)}
                      >
                        View Conference
                      </Button>
                      <Button
                        size="xs"
                        onClick={() => handleAcceptReviewerInvitation(invitation?.id)}
                        loading={acceptingInvitationId === invitation?.id}
                        iconName="Check"
                        iconPosition="left"
                      >
                        Accept Invitation
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Managed Conferences Table */}
          {managedConferences?.length > 0 && (
            <div className="bg-card border border-border mb-6 overflow-hidden shadow-sm">
              <div className="px-4 py-3 border-b border-border flex items-center justify-between bg-muted/40">
                <div className="flex items-center gap-2">
                  <h2 className="text-sm font-semibold text-foreground">{t('userDashboard.managedConferences.title')}</h2>
                  <CountChip count={managedConferences?.length} />
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="table-clear">
                  <thead>
                    <tr>
                      <SortableTh field="name" sort={sortConfig} onSort={handleSort}>
                        {t('userDashboard.managedConferences.headers.name')}
                      </SortableTh>
                      <SortableTh field="status" sort={sortConfig} onSort={handleSort} filter={confFilters.headerProps('status')}>
                        {t('userDashboard.managedConferences.headers.status')}
                      </SortableTh>
                      <SortableTh field="role" sort={sortConfig} onSort={handleSort} filter={confFilters.headerProps('role')}>
                        {t('userDashboard.managedConferences.headers.role')}
                      </SortableTh>
                      <SortableTh field="deadline" sort={sortConfig} onSort={handleSort}>
                        {t('userDashboard.managedConferences.headers.deadline')}
                      </SortableTh>
                      <th className="text-right">{t('userDashboard.managedConferences.headers.actions')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {confPageRows?.map((conference) => (
                      <tr key={conference?.id}>
                        <td>
                          <span className="font-medium text-foreground">{conference?.name}</span>
                        </td>
                        <td>
                          <Badge variant={
                            conference?.status === 'Published' || conference?.status === 'Open' ? 'success'
                              : conference?.status === 'Reviewing' ? 'info'
                              : conference?.status === 'Completed' ? 'default'
                              : 'warning'
                          } size="sm">
                            {getTranslatedStatus(conference?.status)}
                          </Badge>
                        </td>
                        <td>
                          <div className="flex flex-wrap gap-1">
                            {conference?.roles?.map((role, idx) => (
                              <Badge key={idx} size="sm" variant={
                                role === 'chair' ? 'primary' : role === 'organizer' ? 'secondary'
                                  : role === 'reviewer' ? 'info' : 'success'
                              }>
                                {role?.charAt(0)?.toUpperCase() + role?.slice(1)}
                              </Badge>
                            ))}
                          </div>
                        </td>
                        <td>
                          <div className="flex items-center gap-1.5">
                            {/* Format at render, not at load. formatDate() reads
                                the active locale when called, so a string baked
                                into state during load stays in whatever
                                language was active then — dates kept rendering
                                in Arabic after switching to English. */}
                            <span className="text-xs text-muted-foreground tnum">
                              {conference?.deadlineRaw ? formatDate(conference.deadlineRaw) : conference?.deadline}
                            </span>
                            {conference?.priority === 'High' && (
                              <Badge variant="error" size="sm">{t('userDashboard.managedConferences.urgent')}</Badge>
                            )}
                          </div>
                        </td>
                        <td className="text-right">
                          <div className="flex items-center justify-end gap-1.5">
                            <Button variant="outline" size="xs" onClick={() => navigate(`/conference-landing-page?conferenceId=${conference?.id}`)}>
                              {t('userDashboard.managedConferences.view')}
                            </Button>
                            {conference?.canManage && (
                              <Button variant="outline" size="xs" onClick={() => navigate(`/conference-management-dashboard?conferenceId=${conference?.id}`)}>
                                {t('userDashboard.managedConferences.manage')}
                              </Button>
                            )}
                            <BadgeDownloadButton conferenceId={conference?.id} compact />
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <Pagination {...confPager} />
            </div>
          )}

          {/* Paper Submissions */}
          <MySubmissions submissions={userSubmissions} loading={loadingSubmissions} onRefresh={refreshSubmissionsOnly} />

          {/* Reviewer Assignments */}
          {reviewerAssignments?.length > 0 && (
            <div className="bg-card border border-border overflow-hidden shadow-sm mt-6">
              <div className="px-4 py-3 border-b border-border flex items-center justify-between bg-muted/40">
                <div className="flex items-center gap-2">
                  <Icon name="ClipboardCheck" size={15} className="text-primary" />
                  <h2 className="text-sm font-semibold text-foreground">{t('userDashboard.papersToReview.title', 'Papers to review')}</h2>
                  <CountChip
                    count={orderedAssignments?.filter(a => a?.isDraft)?.length || 0}
                    tone="attention"
                  />
                </div>
              </div>
              <div className="divide-y divide-border">
                {orderedAssignments?.map((assignment) => {
                  const daysLeft = Math.ceil((new Date(assignment?.reviewDeadline) - new Date()) / (1000 * 60 * 60 * 24));
                  const isOverdue = daysLeft < 0;
                  const isUrgent = daysLeft >= 0 && daysLeft <= 3;
                  return (
                    <div
                      key={assignment?.reviewId}
                      className={`px-4 py-3 hover:bg-muted/30 transition-colors ${isOverdue ? 'border-l-2 border-l-error' : isUrgent ? 'border-l-2 border-l-warning' : ''}`}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-foreground line-clamp-1">{assignment?.paperTitle}</p>
                          <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-muted-foreground mt-0.5">
                            <span>{assignment?.conferenceName}</span>
                            {assignment?.trackName && <><span>&middot;</span><span>{assignment?.trackName}</span></>}
                            <span>&middot;</span>
                            <span className="flex items-center gap-1">
                              <Icon name="Calendar" size={11} />
                              {/* formatDate() follows the active language; the
                                  hardcoded 'en-US' here printed English dates
                                  inside a French and an Arabic interface. */}
                              {t('userDashboard.papersToReview.due', { date: assignment?.reviewDeadline ? formatDate(assignment?.reviewDeadline) : '—', defaultValue: 'Due {{date}}' })}
                            </span>
                            <span className={`px-1.5 py-0.5 text-[11px] font-medium ${
                              isOverdue ? 'text-error bg-error-subtle'
                              : isUrgent ? 'text-error bg-error-subtle'
                              : daysLeft <= 7 ? 'text-warning bg-warning-subtle'
                              : 'text-success bg-success-subtle'
                            }`}>
                              {isOverdue
                                ? t('userDashboard.papersToReview.overdue', 'Overdue')
                                : t('userDashboard.papersToReview.daysLeft', { count: daysLeft, defaultValue: '{{count}}d left' })}
                            </span>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-medium ${
                            !assignment?.isDraft ? 'bg-success-subtle text-success'
                            : assignment?.overallScore ? 'bg-warning-subtle text-warning'
                            : 'bg-primary-subtle text-primary'
                          }`}>
                            <Icon name={!assignment?.isDraft ? 'CheckCircle' : assignment?.overallScore ? 'Clock' : 'FileText'} size={12} />
                            {!assignment?.isDraft
                              ? t('userDashboard.papersToReview.completed', 'Completed')
                              : assignment?.overallScore
                                ? t('userDashboard.papersToReview.inProgress', 'In progress')
                                : t('userDashboard.papersToReview.notStarted', 'New')}
                          </span>
                          <Button
                            size="xs"
                            variant={!assignment?.isDraft ? 'outline' : 'default'}
                            iconName={!assignment?.isDraft ? 'Eye' : assignment?.overallScore ? 'Edit' : 'Play'}
                            iconPosition="left"
                            onClick={() => handleStartReview(assignment)}
                          >
                            {!assignment?.isDraft
                              ? t('userDashboard.papersToReview.view', 'View')
                              : assignment?.overallScore
                                ? t('userDashboard.papersToReview.continue', 'Continue')
                                : t('userDashboard.papersToReview.start', 'Start')}
                          </Button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Bottom Section: Drafts + Deadlines */}
          {(draftConferences?.length > 0 || deadlines?.length > 0) && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-6">
              {draftConferences?.length > 0 && (
                <div className="bg-card border border-border overflow-hidden shadow-sm">
                  <div className="px-4 py-3 border-b border-border bg-muted/40 flex items-center gap-2">
                    <h3 className="text-sm font-semibold text-foreground">{t('userDashboard.draftConferences.title')}</h3>
                    <CountChip count={draftConferences?.length} />
                  </div>
                  <div className="divide-y divide-border">
                    {draftConferences?.map((draft) => (
                      <div
                        key={draft?.id}
                        className="px-4 py-3 flex items-center justify-between hover:bg-warning-subtle/30 transition-colors cursor-pointer group"
                        onClick={() => navigate(`/conference-setup-wizard?conferenceId=${draft?.id}`)}
                      >
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-foreground truncate">{draft?.name}</p>
                          <p className="text-xs text-muted-foreground">{getTranslatedStatus(draft?.status)}</p>
                        </div>
                        <Icon name="ChevronRight" size={14} className="text-muted-foreground flex-shrink-0" />
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {deadlines?.length > 0 && (
                <UpcomingDeadlines deadlines={deadlines} />
              )}
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default UserDashboard;
